/**
 * /music-stand — the pure part of the stand: no DOM, no storage, no camera.
 * Everything a musician relies on in the middle of a piece lives here, so it
 * is proven by node tests (tests/js/music-stand-*.test.mjs) and not by eye:
 *
 *   · where the music is on the page (white-margin crop, photos included);
 *   · where a page can be cut in two without cutting a staff (half-page
 *     turns and fit-width screens break BETWEEN systems, not through them);
 *   · what is on the screen for a given position (tiles), so the half the
 *     musician is reading never moves when the pedal is pressed;
 *   · which page comes next — halves, spreads, setlists, and repeat jumps
 *     (D.S., D.C., «to Coda») that the pedal follows on the right pass;
 *   · which key is a pedal and which press is a bounce.
 *
 * Coordinates: a page is the unit square — x, y in 0..1 of its width and
 * height. A page's size in points (pw × ph) only matters for aspect.
 */

/* ───────────────────────── image analysis ───────────────────────── */

/**
 * Luma of an RGBA image, transparent pixels composited over white paper
 * (pdf.js leaves unpainted areas transparent; a PNG scan may have alpha).
 */
export function lumaPlane(img) {
    const { data, width: w, height: h } = img;
    const out = new Uint8Array(w * h);
    for (let p = 0, i = 0; p < out.length; p++, i += 4) {
        const l = (data[i] * 77 + data[i + 1] * 150 + data[i + 2] * 29) >> 8;
        const a = data[i + 3];
        out[p] = a === 255 ? l : ((l * a + 255 * (255 - a)) / 255) | 0;
    }
    return out;
}

/**
 * Paper brightness per block. A photographed sheet is not evenly lit — the
 * corner under the phone's shadow is grey — so «ink» is judged against the
 * paper NEAR the pixel, not against one global white. The 90th percentile of
 * a block is its paper: music covers far less than 10 % of any block.
 */
export function backgroundMap(luma, w, h) {
    const bs = Math.max(8, Math.round(Math.min(w, h) / 12));
    const gx = Math.ceil(w / bs), gy = Math.ceil(h / bs);
    const bg = new Float32Array(gx * gy);
    const hist = new Uint32Array(256);
    for (let by = 0; by < gy; by++) {
        for (let bx = 0; bx < gx; bx++) {
            hist.fill(0);
            const x0 = bx * bs, y0 = by * bs, x1 = Math.min(w, x0 + bs), y1 = Math.min(h, y0 + bs);
            let n = 0;
            for (let y = y0; y < y1; y++) {
                const row = y * w;
                for (let x = x0; x < x1; x++) { hist[luma[row + x]]++; n++; }
            }
            const target = n * 0.9;
            let acc = 0, v = 0;
            for (; v < 256; v++) { acc += hist[v]; if (acc >= target) break; }
            bg[by * gx + bx] = Math.min(255, v);
        }
    }
    return { bg, gx, gy, bs };
}

function bgAt(map, x, y) {
    // bilinear between block centres
    const fx = Math.min(map.gx - 1, Math.max(0, x / map.bs - 0.5));
    const fy = Math.min(map.gy - 1, Math.max(0, y / map.bs - 0.5));
    const x0 = fx | 0, y0 = fy | 0;
    const x1 = Math.min(map.gx - 1, x0 + 1), y1 = Math.min(map.gy - 1, y0 + 1);
    const tx = fx - x0, ty = fy - y0, g = map.bg, W = map.gx;
    const a = g[y0 * W + x0] * (1 - tx) + g[y0 * W + x1] * tx;
    const b = g[y1 * W + x0] * (1 - tx) + g[y1 * W + x1] * tx;
    return a * (1 - ty) + b * ty;
}

/**
 * Ink mask: 1 where a pixel is clearly darker than the paper around it.
 * Areas that are not paper at all (the table around a photographed sheet)
 * are never ink — their «paper» is itself dark.
 */
export function inkMask(img) {
    const w = img.width, h = img.height;
    const luma = lumaPlane(img);
    const map = backgroundMap(luma, w, h);
    const sorted = Array.from(map.bg).sort((a, b) => a - b);
    const paper = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.9))] || 255;
    // mask: 0 = paper, 1 = ink on paper, 2 = not paper at all
    const mask = new Uint8Array(w * h);
    let total = 0;
    for (let y = 0; y < h; y++) {
        const row = y * w;
        for (let x = 0; x < w; x++) {
            const b = bgAt(map, x, y);
            if (b < paper * 0.55) { mask[row + x] = 2; continue; }   // table, dark surround
            const l = luma[row + x];
            if (l < b - Math.max(28, b * 0.22)) { mask[row + x] = 1; total++; }
        }
    }
    return { mask, w, h, ink: total / (w * h) };
}

function countsOf(m, win) {
    const { mask, w, h } = m;
    const X0 = win ? win[0] : 0, X1 = win ? win[1] : w, Y0 = win ? win[2] : 0, Y1 = win ? win[3] : h;
    const rows = new Uint32Array(h), cols = new Uint32Array(w);
    const prow = new Uint32Array(h), pcol = new Uint32Array(w);
    for (let y = Y0; y < Y1; y++) {
        const row = y * w;
        let c = 0, p = 0;
        for (let x = X0; x < X1; x++) {
            const v = mask[row + x];
            if (v === 1) { c++; cols[x]++; }
            if (v !== 2) { p++; pcol[x]++; }
        }
        rows[y] = c; prow[y] = p;
    }
    return { rows, cols, prow, pcol };
}

/** First/last index with content, skipping what is not paper, a dark edge band and the white margin. */
function span(counts, paper, len, cross) {
    const minRun = Math.max(3, Math.round(cross * 0.008));
    const heavy = cross * 0.5;
    const lim = Math.floor(len * 0.12);
    let a = 0, b = len - 1;
    // outside the sheet (a table around a photographed page)
    while (a < b && paper[a] < cross * 0.5) a++;
    while (b > a && paper[b] < cross * 0.5) b--;
    // A scanner's black edge, a gutter shadow, the rim of a photographed
    // sheet: a band of nearly solid ink where the paper BEGINS. Staff lines
    // are long too, but an engraved page never starts with one at its edge.
    if (counts[a] >= heavy) { const z = a + lim; while (a < z && counts[a] >= heavy * 0.6) a++; }
    if (counts[b] >= heavy) { const z = b - lim; while (b > z && counts[b] >= heavy * 0.6) b--; }
    while (a <= b && counts[a] < minRun) a++;
    while (b >= a && counts[b] < minRun) b--;
    return a > b ? null : [a, b + 1];
}

/**
 * The box around the music, in page fractions, with a little air around it.
 * Falls back to the whole page when there is nothing to gain (no margins) or
 * nothing to trust (a blank page, a photo that is mostly texture).
 */
export function contentBox(img, opts = {}) {
    const pad = opts.pad ?? 0.015;
    const m = img.mask ? img : inkMask(img);
    const full = { x: 0, y: 0, w: 1, h: 1, full: true };
    if (m.ink < 0.0005 || m.ink > 0.45) return full;
    // Rows are judged inside the found columns and columns inside the found
    // rows — otherwise the rim of a photographed sheet running down its side
    // puts «ink» in every row and no row ever reads as margin.
    let c = countsOf(m);
    let xs = span(c.cols, c.pcol, m.w, m.h), ys = span(c.rows, c.prow, m.h, m.w);
    for (let pass = 0; pass < 2 && xs && ys; pass++) {
        c = countsOf(m, [xs[0], xs[1], 0, m.h]);
        ys = span(c.rows, c.prow, m.h, xs[1] - xs[0]);
        if (!ys) break;
        c = countsOf(m, [0, m.w, ys[0], ys[1]]);
        xs = span(c.cols, c.pcol, m.w, ys[1] - ys[0]);
    }
    if (!ys || !xs) return full;
    let x0 = xs[0] / m.w - pad, x1 = xs[1] / m.w + pad;
    let y0 = ys[0] / m.h - pad, y1 = ys[1] / m.h + pad;
    x0 = Math.max(0, x0); y0 = Math.max(0, y0); x1 = Math.min(1, x1); y1 = Math.min(1, y1);
    const bw = x1 - x0, bh = y1 - y0;
    if (bw < 0.2 || bh < 0.08) return full;                  // a speck, not a page of music
    if (bw > 0.97 && bh > 0.97) return full;
    return { x: r4(x0), y: r4(y0), w: r4(bw), h: r4(bh) };
}

/**
 * Ink per row across the whole page height, measured only inside the
 * content columns, quantised to bytes (step 1/1020 — a gap between systems
 * reads 0, a staff line saturates; only the quiet end matters).
 */
export function rowProfile(img, box, bins = 512) {
    const m = img.mask ? img : inkMask(img);
    const out = new Uint8Array(bins);
    const x0 = Math.floor((box ? box.x : 0) * m.w), x1 = Math.max(x0 + 1, Math.ceil((box ? box.x + box.w : 1) * m.w));
    const cw = x1 - x0;
    for (let b = 0; b < bins; b++) {
        const ya = Math.floor(b / bins * m.h), yb = Math.max(ya + 1, Math.floor((b + 1) / bins * m.h));
        let c = 0, n = 0;
        for (let y = ya; y < yb && y < m.h; y++) {
            const row = y * m.w;
            for (let x = x0; x < x1; x++) if (m.mask[row + x] === 1) c++;
            n += cw;
        }
        out[b] = Math.min(255, Math.round((n ? c / n : 0) * 1020));
    }
    return out;
}

const r4 = (v) => Math.round(v * 10000) / 10000;

/**
 * Same-size boxes for every page of a piece. Per-page crops make the notes
 * change size from page to page (a half-empty last page would be blown up
 * twice) — a musician reads that as the page «jumping». So every page gets
 * the largest width and height, centred horizontally on its own music and
 * aligned to the top of it, and pushed back inside the page.
 */
export function uniformBoxes(boxes) {
    const real = boxes.filter((b) => b && !b.full);
    if (!real.length) return boxes.map(() => ({ x: 0, y: 0, w: 1, h: 1, full: true }));
    const W = Math.min(1, Math.max(...real.map((b) => b.w)));
    const H = Math.min(1, Math.max(...real.map((b) => b.h)));
    return boxes.map((b) => {
        if (!b || b.full) return { x: 0, y: 0, w: 1, h: 1, full: true };
        let x = b.x + b.w / 2 - W / 2, y = b.y;
        x = Math.min(Math.max(0, x), 1 - W);
        y = Math.min(Math.max(0, y), 1 - H);
        return { x: r4(x), y: r4(y), w: r4(W), h: r4(H) };
    });
}

/* ───────────────────────── where to cut a page ───────────────────────── */

function quietLevel(profile, lo, hi) {
    const vals = [];
    for (let i = lo; i < hi; i++) vals.push(profile[i]);
    if (!vals.length) return 4;
    vals.sort((a, b) => a - b);
    const p90 = vals[Math.floor(vals.length * 0.9)] || 0;
    return Math.max(4, p90 * 0.06);
}

function runsBelow(profile, i0, i1, q) {
    const runs = [];
    let s = -1;
    for (let i = i0; i <= i1; i++) {
        const quiet = i < i1 && profile[i] <= q;
        if (quiet && s < 0) s = i;
        else if (!quiet && s >= 0) { runs.push([s, i]); s = -1; }
    }
    return runs;
}

/**
 * Where a half-page turn splits a page: the widest quiet band between two
 * systems near the middle of the music. Returns a page fraction.
 *   profile — rowProfile() bytes across the page height
 *   box     — the content box (the split lives inside it)
 */
export function findSplit(profile, box, opts = {}) {
    const NB = profile.length;
    const b = box || { y: 0, h: 1 };
    const target = b.y + b.h * (opts.target ?? 0.5);
    const lo = b.y + b.h * (opts.lo ?? 0.28), hi = b.y + b.h * (opts.hi ?? 0.72);
    const c0 = Math.max(0, Math.floor(b.y * NB)), c1 = Math.min(NB, Math.ceil((b.y + b.h) * NB));
    const q = quietLevel(profile, c0, c1);
    let best = null, bestScore = -Infinity;
    // runs over the whole music, kept when their CENTRE is in the window — a
    // gap that crosses the window's edge is measured whole, not truncated
    for (const [s, e] of runsBelow(profile, c0, c1, q)) {
        const c = (s + e) / 2 / NB;
        if (c < lo || c > hi) continue;
        const len = (e - s) / NB / b.h;
        const score = len * 3 - Math.abs(c - target) / b.h;
        if (score > bestScore) { bestScore = score; best = c; }
    }
    if (best === null) {
        let mi = -1, mv = Infinity;
        const a = Math.floor((b.y + b.h * 0.4) * NB), z = Math.ceil((b.y + b.h * 0.6) * NB);
        for (let i = a; i < z; i++) if (profile[i] < mv) { mv = profile[i]; mi = i; }
        best = mi >= 0 ? (mi + 0.5) / NB : target;
    }
    return r4(best);
}

/**
 * Screens for «fit width»: the music of one page cut into pieces that each
 * fit the screen, cut in quiet bands between systems. `maxSpan` is how much
 * page height (fraction) one screen shows. Returns [[y0, y1], …].
 */
export function segmentBreaks(profile, box, maxSpan) {
    const NB = profile.length;
    const b = box || { y: 0, h: 1 };
    const top = b.y, bottom = b.y + b.h;
    if (!(maxSpan > 0) || bottom - top <= maxSpan + 1e-6) return [[r4(top), r4(bottom)]];
    const q = quietLevel(profile, Math.floor(top * NB), Math.ceil(bottom * NB));
    const out = [];
    let s = top, guard = 0;
    const runs = runsBelow(profile, Math.max(0, Math.floor(top * NB)), Math.min(NB, Math.ceil(bottom * NB)), q);
    while (bottom - s > maxSpan + 1e-6 && guard++ < 64) {
        const limit = s + maxSpan, from = s + maxSpan * 0.45;
        let cut = null, bestScore = -Infinity;
        for (const [rs, re] of runs) {
            const a = rs / NB, z = re / NB;
            if (z <= from || a >= limit) continue;
            // cut in the middle of the gap, or at the screen's edge if the gap
            // runs past it (still quiet there)
            const c = Math.min(Math.max((a + z) / 2, from), limit);
            const inWin = Math.min(z, limit) - Math.max(a, from);
            const score = inWin / maxSpan - (limit - c) / maxSpan * 0.5;
            if (score > bestScore) { bestScore = score; cut = c; }
        }
        if (cut === null) {
            // no gap — cut at the screen's edge and repeat a sliver on the next one
            out.push([r4(s), r4(limit)]);
            s = limit - maxSpan * 0.06;
        } else {
            out.push([r4(s), r4(cut)]);
            s = cut;
        }
    }
    out.push([r4(s), r4(bottom)]);
    return out;
}

/* ───────────────────────── what is on the screen ───────────────────────── */

const FULL = { x: 0, y: 0, w: 1, h: 1 };

function fit(meta, W, H) {
    const b = meta.box || FULL;
    const nw = b.w * meta.pw, nh = b.h * meta.ph;
    const s = Math.min(W / nw, H / nh);
    const dw = nw * s, dh = nh * s;
    return { s, dx: (W - dw) / 2, dy: (H - dh) / 2, dw, dh, b };
}

function splitOf(meta) {
    const b = meta.box || FULL;
    const sp = meta.split ?? (b.y + b.h / 2);
    return Math.min(b.y + b.h * 0.92, Math.max(b.y + b.h * 0.08, sp));
}

/**
 * Tiles for one view. Each tile draws a source rectangle of a page
 * (sx, sy, sw, sh — page fractions) into a screen rectangle (dx, dy, dw, dh).
 *   view.kind: 'page' | 'half' | 'seg' | 'spread'
 *   metaOf(i) → { pw, ph, box, split }
 *   opts.halfStyle: 'keep' (default) | 'scroll'
 */
export function layoutView(view, metaOf, W, H, opts = {}) {
    const tiles = [];
    let divider = null;
    const tile = (i, sx, sy, sw, sh, dx, dy, dw, dh) => tiles.push({ i, sx, sy, sw, sh, dx, dy, dw, dh });

    if (view.kind === 'page') {
        const m = metaOf(view.i), f = fit(m, W, H);
        tile(view.i, f.b.x, f.b.y, f.b.w, f.b.h, f.dx, f.dy, f.dw, f.dh);
    } else if (view.kind === 'seg') {
        const m = metaOf(view.i), b = m.box || FULL;
        const y0 = view.y0, y1 = view.y1;
        let s = W / (b.w * m.pw);
        if ((y1 - y0) * m.ph * s > H) s = H / ((y1 - y0) * m.ph);
        const dw = b.w * m.pw * s, dh = (y1 - y0) * m.ph * s;
        tile(view.i, b.x, y0, b.w, y1 - y0, (W - dw) / 2, 0, dw, dh);
    } else if (view.kind === 'spread') {
        const a = metaOf(view.i), bm = view.j != null ? metaOf(view.j) : null;
        const ba = a.box || FULL;
        const wA = ba.w * a.pw, hA = ba.h * a.ph;
        const gap = Math.round(W * 0.012);
        if (!bm) {
            // the last odd page: same size as it would be in a spread, centred
            const s = Math.min((W - gap) / (2 * wA), H / hA);
            tile(view.i, ba.x, ba.y, ba.w, ba.h, (W - wA * s) / 2, (H - hA * s) / 2, wA * s, hA * s);
        } else {
            const bb = bm.box || FULL;
            const wB = bb.w * bm.pw, hB = bb.h * bm.ph;
            const s = Math.min((W - gap) / (wA + wB), H / Math.max(hA, hB));
            const total = (wA + wB) * s + gap;
            const x0 = (W - total) / 2;
            tile(view.i, ba.x, ba.y, ba.w, ba.h, x0, (H - hA * s) / 2, wA * s, hA * s);
            tile(view.j, bb.x, bb.y, bb.w, bb.h, x0 + wA * s + gap, (H - hB * s) / 2, wB * s, hB * s);
        }
    } else if (view.kind === 'half') {
        const mi = metaOf(view.i), mj = metaOf(view.j);
        const bi = mi.box || FULL, bj = mj.box || FULL;
        const si = splitOf(mi), sj = splitOf(mj);
        const A = fit(mi, W, H), B = fit(mj, W, H);
        const gap = 3;
        if ((opts.halfStyle || 'keep') === 'scroll') {
            // the rest of this page moves up, the top of the next one comes in below
            const hb = (bi.y + bi.h - si) * mi.ph, wb = bi.w * mi.pw;
            const ht = (sj - bj.y) * mj.ph, wt = bj.w * mj.pw;
            const s = Math.min(A.s, W / Math.max(wb, wt), (H - gap) / (hb + ht));
            const y0 = Math.max(0, (H - (hb + ht) * s - gap) / 2);
            tile(view.i, bi.x, si, bi.w, bi.y + bi.h - si, (W - wb * s) / 2, y0, wb * s, hb * s);
            const yd = y0 + hb * s + gap / 2;
            tile(view.j, bj.x, bj.y, bj.w, sj - bj.y, (W - wt * s) / 2, y0 + hb * s + gap, wt * s, ht * s);
            divider = { y: yd, x0: Math.min((W - wb * s) / 2, (W - wt * s) / 2), x1: Math.max((W + wb * s) / 2, (W + wt * s) / 2), style: 'scroll' };
        } else {
            // KEEP YOUR PLACE: the bottom of this page stays exactly where it
            // was; the top of the next page appears exactly where it will be
            // after the second press — nothing the musician is reading moves.
            const ySplit = A.dy + (si - bi.y) * mi.ph * A.s;
            tile(view.i, bi.x, si, bi.w, bi.y + bi.h - si, A.dx, ySplit, A.dw, (bi.y + bi.h - si) * mi.ph * A.s);
            let dh = (sj - bj.y) * mj.ph * B.s, dw = B.dw, dx = B.dx;
            const room = ySplit - gap - B.dy;
            if (dh > room && room > 0) {
                // the next page's top half is taller than the room above the
                // divider — shrink it a little rather than hide unplayed music
                const f = room / dh;
                dh *= f; dx = B.dx + (dw - dw * f) / 2; dw *= f;
            }
            tile(view.j, bj.x, bj.y, bj.w, sj - bj.y, dx, B.dy, dw, dh);
            divider = { y: ySplit - gap / 2, x0: Math.min(A.dx, B.dx), x1: Math.max(A.dx + A.dw, B.dx + B.dw), style: 'keep' };
        }
    }
    return { tiles, divider };
}

/** Screen point → {i, u, v} on a page (page fractions), or null. */
export function screenToPage(tiles, x, y) {
    for (let k = tiles.length - 1; k >= 0; k--) {
        const t = tiles[k];
        if (x >= t.dx && x <= t.dx + t.dw && y >= t.dy && y <= t.dy + t.dh) {
            return { i: t.i, u: t.sx + (x - t.dx) / t.dw * t.sw, v: t.sy + (y - t.dy) / t.dh * t.sh, tile: t };
        }
    }
    return null;
}

/** Page point → screen point through a tile, or null when the tile does not show it. */
export function pageToScreen(tile, u, v) {
    if (u < tile.sx - 1e-6 || u > tile.sx + tile.sw + 1e-6 || v < tile.sy - 1e-6 || v > tile.sy + tile.sh + 1e-6) return null;
    return { x: tile.dx + (u - tile.sx) / tile.sw * tile.dw, y: tile.dy + (v - tile.sy) / tile.sh * tile.dh };
}

/* ───────────────────────── which page comes next ───────────────────────── */

/**
 * The navigator walks a flat list of pages (one piece, or a whole setlist).
 * ctx (read live, so a rotation or a setting applies at once):
 *   count                       pages in the list
 *   pieceStart(i), pieceEnd(i)  bounds of the piece page i belongs to
 *   mode                        'page' | 'width' | 'spread'
 *   half                        half-page turns (mode 'page')
 *   spreadStep                  2 = like a book, 1 = one page at a time
 *   segs(i)                     [[y0,y1], …] screens of page i ('width')
 *   links(i)                    [{id, to, pass}] repeat jumps leaving page i;
 *                               `to` is a list index, `pass` the time through
 *                               this page on which the pedal follows it
 *                               (0 = only when tapped)
 *
 * «Back» first undoes the last forward move exactly (after a D.S. it goes
 * back to where the jump came from, and the pass counter is restored — an
 * accidental press and a correction never skip a repeat).
 */
export class Navigator {
    constructor(ctx) {
        this.ctx = ctx;
        this.st = { i: 0, part: 0, j: null };
        this.hist = [];
        this.visits = new Map();
    }

    get state() { return { ...this.st }; }

    _parts(i) {
        const c = this.ctx;
        if (c.mode === 'width') return Math.max(1, (c.segs ? c.segs(i) : [[0, 1]]).length);
        if (c.mode === 'page' && c.half) return 2;
        return 1;
    }

    _spreadMax(i) {
        const c = this.ctx, ps = c.pieceStart(i), pe = c.pieceEnd(i);
        if ((c.spreadStep || 2) === 1) return Math.max(ps, pe - 2);
        return ps + Math.floor((pe - 1 - ps) / 2) * 2;
    }

    _spreadAlign(i) {
        const c = this.ctx, ps = c.pieceStart(i);
        if ((c.spreadStep || 2) === 1) return Math.min(i, this._spreadMax(i));
        return ps + Math.floor((i - ps) / 2) * 2;
    }

    /** The auto jump leaving page i on this pass, if any. */
    _autoLink(i) {
        const links = this.ctx.links ? this.ctx.links(i) : [];
        const pass = (this.visits.get(i) || 0) + 1;
        return links.find((l) => l.pass && l.pass === pass && l.to >= 0 && l.to < this.ctx.count) || null;
    }

    /** Where leaving page i forward lands (list index), without moving. */
    _successor(i) {
        const link = this._autoLink(i);
        if (link) return { to: link.to, link };
        if (i + 1 < this.ctx.count) return { to: i + 1, link: null };
        return null;
    }

    view() {
        const c = this.ctx, s = this.st;
        if (c.mode === 'spread') {
            const j = s.i + 1 < c.pieceEnd(s.i) ? s.i + 1 : null;
            return { kind: 'spread', i: s.i, j };
        }
        if (c.mode === 'width') {
            const segs = c.segs ? c.segs(s.i) : [[0, 1]];
            const seg = segs[Math.min(s.part, segs.length - 1)] || [0, 1];
            return { kind: 'seg', i: s.i, part: s.part, parts: segs.length, y0: seg[0], y1: seg[1] };
        }
        if (s.part === 1 && s.j != null) return { kind: 'half', i: s.i, j: s.j };
        return { kind: 'page', i: s.i };
    }

    _push() { this.hist.push({ st: { ...this.st } }); if (this.hist.length > 400) this.hist.shift(); }

    _depart(i) { this.visits.set(i, (this.visits.get(i) || 0) + 1); this.hist[this.hist.length - 1].visit = i; }

    next() {
        const c = this.ctx, s = this.st;
        if (c.mode === 'spread') {
            const last = s.i + 1 < c.pieceEnd(s.i) ? s.i + 1 : s.i;
            const link = this._autoLink(last);
            let to;
            if (link) to = this._spreadAlign(link.to);
            else if (s.i + (c.spreadStep || 2) <= this._spreadMax(s.i)) to = s.i + (c.spreadStep || 2);
            else if (c.pieceEnd(s.i) < c.count) to = c.pieceEnd(s.i);
            else return { moved: false, end: true };
            this._push(); this._depart(last);
            const crossed = c.pieceStart(to) !== c.pieceStart(s.i);
            this.st = { i: to, part: 0, j: null };
            return { moved: true, via: link ? 'link' : (crossed ? 'piece' : 'turn'), link };
        }
        if (c.mode === 'page' && c.half) {
            if (s.part === 0) {
                const succ = this._successor(s.i);
                if (!succ) return { moved: false, end: true };
                // half turns never cross into the next piece — there is a pause
                // between pieces, and its first page deserves the whole screen
                if (c.pieceStart(succ.to) !== c.pieceStart(s.i) && !succ.link) {
                    this._push(); this._depart(s.i);
                    this.st = { i: succ.to, part: 0, j: null };
                    return { moved: true, via: 'piece' };
                }
                this._push(); this._depart(s.i);
                this.st = { i: s.i, part: 1, j: succ.to };
                return { moved: true, via: succ.link ? 'link' : 'half', link: succ.link };
            }
            this._push();
            this.st = { i: s.j, part: 0, j: null };
            return { moved: true, via: 'turn' };
        }
        const parts = this._parts(s.i);
        if (s.part + 1 < parts) {
            this._push();
            this.st = { i: s.i, part: s.part + 1, j: null };
            return { moved: true, via: 'scroll' };
        }
        const succ = this._successor(s.i);
        if (!succ) return { moved: false, end: true };
        this._push(); this._depart(s.i);
        const crossed = c.pieceStart(succ.to) !== c.pieceStart(s.i);
        this.st = { i: succ.to, part: 0, j: null };
        return { moved: true, via: succ.link ? 'link' : (crossed ? 'piece' : 'turn'), link: succ.link };
    }

    prev() {
        const h = this.hist.pop();
        if (h) {
            if (h.visit != null) this.visits.set(h.visit, Math.max(0, (this.visits.get(h.visit) || 1) - 1));
            this.st = this.normalize(h.st);
            return { moved: true, via: 'undo' };
        }
        const c = this.ctx, s = this.st;
        if (c.mode === 'spread') {
            const ps = c.pieceStart(s.i), step = c.spreadStep || 2;
            if (s.i - step >= ps) this.st = { i: s.i - step, part: 0, j: null };
            else if (s.i > ps) this.st = { i: ps, part: 0, j: null };
            else if (ps > 0) this.st = { i: this._spreadMax(ps - 1), part: 0, j: null };
            else return { moved: false, start: true };
            return { moved: true, via: 'back' };
        }
        if (s.part > 0) {
            this.st = { i: s.i, part: s.part - 1, j: null };
            return { moved: true, via: 'back' };
        }
        if (s.i === 0) return { moved: false, start: true };
        const i = s.i - 1;
        if (c.mode === 'page' && c.half && c.pieceStart(i) === c.pieceStart(s.i)) this.st = { i, part: 1, j: s.i };
        else this.st = { i, part: Math.max(0, this._parts(i) - 1), j: null };
        return { moved: true, via: 'back' };
    }

    /** Jump to page i (a tap on a link chip, the page menu, a setlist item). */
    goTo(i, opts = {}) {
        if (!(i >= 0 && i < this.ctx.count)) return false;
        if (opts.push !== false) this._push();
        if (opts.resetPasses) this.visits.clear();
        this.st = this.normalize({ i, part: 0, j: null });
        return true;
    }

    resetPasses() { this.visits.clear(); this.hist.length = 0; }

    /** A state made valid for the current mode (after a rotation or a setting). */
    normalize(st) {
        const c = this.ctx;
        let i = Math.min(Math.max(0, st.i | 0), Math.max(0, c.count - 1));
        if (c.mode === 'spread') return { i: this._spreadAlign(i), part: 0, j: null };
        if (c.mode === 'page') {
            if (c.half && st.part === 1 && st.j != null && st.j < c.count) return { i, part: 1, j: st.j };
            return { i, part: 0, j: null };
        }
        return { i, part: Math.min(st.part || 0, this._parts(i) - 1), j: null };
    }

    refresh() { this.st = this.normalize(this.st); }
}

/* ───────────────────────── pedals and keys ───────────────────────── */

/**
 * Bluetooth page turners are keyboards. AirTurn, PageFlip, iRig BlueTurn and
 * the no-name pedals ship modes that send one of these pairs — arrows left/
 * right, arrows up/down, Page Up/Page Down, Space/Enter — so all of them
 * work without setup; an odd pedal is taught its keys with «learn».
 */
export const DEFAULT_KEYS = {
    next: ['ArrowRight', 'ArrowDown', 'PageDown', 'Space', 'Enter', 'NumpadEnter'],
    prev: ['ArrowLeft', 'ArrowUp', 'PageUp', 'Backspace', 'Shift+Space'],
};

export function keyId(e) {
    let id = e.code || '';
    if (!id) {
        const k = e.key || '';
        id = k === ' ' ? 'Space' : k;
    }
    if (id === 'Space' && e.shiftKey) return 'Shift+Space';
    return id;
}

export function actionForKey(id, map) {
    const m = map || DEFAULT_KEYS;
    if ((m.next || []).includes(id)) return 'next';
    if ((m.prev || []).includes(id)) return 'prev';
    return null;
}

/**
 * A pedal that bounces sends two presses 30–80 ms apart; a musician's real
 * double press is never faster than ~200 ms. `gapMs` presses closer than
 * that to the last ACCEPTED one are dropped.
 */
export function createDebouncer(gapMs = 250) {
    let last = -Infinity;
    return (t) => {
        if (t - last < gapMs) return false;
        last = t;
        return true;
    };
}

/* ───────────────────────── library helpers ───────────────────────── */

const collator = typeof Intl !== 'undefined' ? new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }) : null;

/** IMG_2 before IMG_10 — photos of pages are named in shooting order. */
export function naturalCompare(a, b) {
    return collator ? collator.compare(a, b) : (a < b ? -1 : a > b ? 1 : 0);
}

function baseName(n) {
    return String(n || '').replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[_]+/g, ' ').trim();
}

/**
 * A name for a new piece from its files: the file name for one file, the
 * shared start of the names for a set of photos («Ave Maria p1», «Ave Maria
 * p2» → «Ave Maria»), otherwise the first name.
 */
export function pieceName(names) {
    const list = (names || []).map(baseName).filter(Boolean);
    if (!list.length) return '';
    if (list.length === 1) return list[0];
    let p = list[0];
    for (const n of list.slice(1)) {
        let k = 0;
        while (k < p.length && k < n.length && p[k].toLowerCase() === n[k].toLowerCase()) k++;
        p = p.slice(0, k);
    }
    p = p.replace(/[\s\-–—.,(#]*(p|pg|page|стр|с|s|seite|página|pagina|hal|페이지)?[\s\-–—.,(#]*\d*$/i, '').trim();
    return p.length >= 3 ? p : list[0];
}

/* ───────────────────────── annotations ───────────────────────── */

function segDist2(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const l2 = dx * dx + dy * dy;
    let t = l2 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
    t = Math.max(0, Math.min(1, t));
    const x = ax + t * dx - px, y = ay + t * dy - py;
    return x * x + y * y;
}

/**
 * Which marks an eraser path touches. Page coordinates are fractions of
 * width and height, so distances are measured in page-width units
 * (`aspect` = ph / pw rescales y).
 *   items — [{t:'pen'|'hl', p:[x,y,…]} | {t:'txt', x, y, z, s}]
 *   path  — [x,y,…] eraser points; r — radius in page-width units
 * Returns the indices to delete.
 */
export function eraseHits(items, path, r, aspect = 1.414) {
    const hit = [];
    const r2 = r * r;
    for (let k = 0; k < items.length; k++) {
        const it = items[k];
        let touched = false;
        if (it.t === 'txt') {
            const half = (it.z || 0.02) * aspect * 0.6 + r;
            for (let e = 0; e < path.length && !touched; e += 2) {
                const dx = path[e] - it.x, dy = (path[e + 1] - it.y) * aspect;
                if (Math.abs(dx) <= half + (String(it.s || '').length * (it.z || 0.02) * aspect * 0.3) && Math.abs(dy) <= half) touched = true;
            }
        } else {
            const p = it.p || [];
            const rr = r2 + Math.pow((it.w || 0.003) / 2, 2);
            for (let e = 0; e < path.length && !touched; e += 2) {
                const ex = path[e], ey = path[e + 1] * aspect;
                if (p.length === 2) {
                    const dx = p[0] - ex, dy = p[1] * aspect - ey;
                    if (dx * dx + dy * dy <= rr) touched = true;
                }
                for (let q = 0; q + 3 < p.length && !touched; q += 2) {
                    if (segDist2(ex, ey, p[q], p[q + 1] * aspect, p[q + 2], p[q + 3] * aspect) <= rr) touched = true;
                }
            }
        }
        if (touched) hit.push(k);
    }
    return hit;
}

/** Drop points closer than `eps` to the previous kept one (page units) — smaller storage, same line. */
export function simplifyPoints(p, eps = 0.0015) {
    if (p.length <= 4) return p.slice();
    const out = [p[0], p[1]];
    for (let k = 2; k < p.length - 2; k += 2) {
        const dx = p[k] - out[out.length - 2], dy = p[k + 1] - out[out.length - 1];
        if (dx * dx + dy * dy >= eps * eps) out.push(p[k], p[k + 1]);
    }
    out.push(p[p.length - 2], p[p.length - 1]);
    return out.map((v) => Math.round(v * 10000) / 10000);
}
